import type { TFunction } from 'i18next';
import type { Format, PublicEvent } from '@gg/shared';
import { formatDateRange } from './dates';
import i18n from './i18n';

/** A format's name in the visitor's language: French when no English name is set (A-01). */
export const localName = (fr: string, en: string | null, locale = i18n.language): string =>
  locale === 'en' && en ? en : fr;

/** Team format (P-20): bought alone; the captain names the team, teammates pay their place by link. */
export const isTeamFormat = (f: Pick<Format, 'registrationMode'>) => f.registrationMode === 'team';

/**
 * Next selection after toggling `f` (screen 01): a team format is the order's only format, so
 * it replaces the selection and is dropped when an individual format is picked.
 */
export function toggleSelection(selected: string[], formats: Format[], f: Format, on: boolean) {
  if (!on) return selected.filter((id) => id !== f.id);
  if (isTeamFormat(f)) return [f.id];
  const teamIds = formats.filter(isTeamFormat).map((t) => t.id);
  return [...selected.filter((id) => !teamIds.includes(id)), f.id];
}

/** On sale and not sold out. */
export const isPurchasable = (f: Format) => Boolean(f.currentTier) && f.remainingSpots !== 0;

/** Current tier cheaper than the standard price: early-bird badge + struck standard price. */
export const isDiscounted = (f: Format) =>
  f.currentTier !== null &&
  f.standardPriceCents !== null &&
  f.currentTier.priceCents < f.standardPriceCents;

/** "Gravel Games 2027 · 12–13 juin · Vercors" (dates and place only when set). */
export function eventEyebrow(event: PublicEvent): string {
  return [
    `${event.name} ${event.edition}`,
    formatDateRange(event.startsAt, event.endsAt),
    event.location,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** "1 / 2 membres", or "3 membres (2 minimum)" for an unlimited team (screen 06). */
export function membersLabel(
  t: TFunction,
  joined: number,
  sizeMax: number | null,
  sizeMin: number,
) {
  return sizeMax === null
    ? t('teams.membersOpen', { joined, min: sizeMin })
    : t('teams.members', { joined, size: sizeMax });
}

/** "Équipe de 2", "Équipe de 2 à 4" or "Équipe de 2 ou plus" (screen 01, team formats). */
export function teamSizeLabel(
  t: TFunction,
  f: { teamSizeMin: number | null; teamSizeMax: number | null },
): string {
  const min = f.teamSizeMin ?? f.teamSizeMax ?? 1;
  const max = f.teamSizeMax;
  if (max === null) return t('formats.teamOpen', { min });
  return min === max ? t('formats.team', { size: max }) : t('formats.teamRange', { min, max });
}
